import async from 'async';
import _ from 'underscore';

import { I9nHelper } from '@biteinc/helpers';

import { convertCsvLinesToCsvData } from '../../csv_builder';
import { i9nBadgesHtml } from '../../helpers/html_builder';
import { TimeHelper } from '../../helpers/time_helper';
import { getCsvLinesFromOrgList } from '../../org_list_csv_builder';

app.OrgListView = app.BaseListView.extend({
  title: 'All Our Orgs',

  initialize(options) {
    options.cellOptions = {
      getNameUrl(org) {
        const orgName = org.displayName();
        let orgNameHtml = orgName;
        const parts = [];
        if (org.liveLocationCount) {
          parts.push(`${org.liveLocationCount} live ${i9nBadgesHtml(org.liveI9nSchemas)}`);
          if (org.nonLiveI9nSchemas.length) {
            parts.push(`draft ${i9nBadgesHtml(org.nonLiveI9nSchemas)}`);
          }
        } else if (org.nonLiveI9nSchemas.length) {
          parts.push(i9nBadgesHtml(org.nonLiveI9nSchemas));
        }
        if (parts.length) {
          orgNameHtml += ` [${parts.join(' | ')}]`;
        }
        return {
          href: `/${org.get('normalizedName')}`,
          alt: orgName,
          innerHtml: orgNameHtml,
        };
      },
    };

    options.detailsView = app.NewOrgDetailsView;

    app.BaseListView.prototype.initialize.apply(this, arguments);
  },

  createNew() {
    this._createOrgFromButtonAndOrgId();
  },

  getListSections() {
    const liveOrgs = [];
    const draftOrgs = [];
    const churnedOrgs = [];
    this.collection.models.forEach((org) => {
      if (org.get('isLive')) {
        liveOrgs.push(org);
        return;
      }
      if (org.get('isChurned')) {
        churnedOrgs.push(org);
        return;
      }
      draftOrgs.push(org);
    });
    return [
      {
        header: 'Live Accounts',
        models: liveOrgs,
      },
      {
        header: 'Non-Live Accounts',
        models: draftOrgs,
      },
      {
        header: 'Churned Accounts',
        models: churnedOrgs,
      },
    ];
  },

  _renderSearchHint() {
    const keywordsByLabel = {
      POS: I9nHelper.getSearchTokensWithTypes(['pos', 'pos-data-source'], ':standalone'),
      Fulfillment: I9nHelper.getSearchTokensWithTypes(['fulfillment']),
      Payments: I9nHelper.getSearchTokensWithTypes([
        'ecomm-payment',
        'kiosk-payment',
        'cash-machine',
      ]),
      Loyalty: I9nHelper.getSearchTokensWithTypes(['loyalty', 'loyalty-earner', '']),
      'Stored Value': I9nHelper.getSearchTokensWithTypes([
        'stored-value',
        'comp-card',
        'campus-card',
      ]),
      Marketing: I9nHelper.getSearchTokensWithTypes(['marketing']),
      'Table Tracker': I9nHelper.getSearchTokensWithTypes(['table-tracker']),
    };

    const countByKeyword = {};
    if (this.collection.hasBeenFetched()) {
      _.each(keywordsByLabel, (keywords) => {
        _.each(keywords, (keyword) => {
          const count = this.collection.filter((org) => {
            return org.locations.some((location) => {
              return location.matchesQuery(keyword);
            });
          }).length;
          countByKeyword[keyword] = count;
        });
      });
    }

    app.HtmlHelper.renderSearchHints({
      keywordsByLabel,
      keywordUsageExample: ':olo :worldpay for all orgs with a location on both olo and WP',
      $searchHint: this.$searchHint,
      $searchField: this.$searchField,
      searchFieldEventToTriggerOnTokenClick: 'search',
      countByKeyword,
      skipZeroCounts: true,
    });
  },

  _createOrgFromButtonAndOrgId() {
    const model = new this.collection.model(
      {},
      {
        collection: this.collection,
      },
    );
    this.editModel(model);
  },

  // Reliably start the download and allows us to set the file name.
  _exportOrgList() {
    const query = this._getCurrentQuery();

    async.auto(
      {
        reportOptions: (cb) => {
          if (!query) {
            cb(null, {});
            return;
          }

          const detailsView = new app.SchemaDetailsView({
            schema: {
              type: 'object',
              fields: {
                countLiveLocationsThatMatchQuery: {
                  displayName: 'Count only live locations that match your query',
                  type: 'bool',
                  defaultValue: false,
                },
              },
            },
            title: 'Options',
            saveButtonTitles: ['Download', 'Downloading', 'Done!'],
            subProperty: 'options',
            callback: (model) => {
              cb(null, model.options || {});
            },
          });
          app.modalManager.showModalWithView(detailsView);
        },
        csvLines: [
          'reportOptions',
          (results, cb) => {
            getCsvLinesFromOrgList(
              this.collection,
              query,
              results.reportOptions.countLiveLocationsThatMatchQuery,
              cb,
            );
          },
        ],
      },
      (err, results) => {
        if (err) {
          return;
        }

        const timestamp = TimeHelper.format(Date.now(), 'YYYY-MM-DD-HH-mm');
        const aLink = document.createElement('a');
        aLink.download = `bite-org-list-${timestamp}.csv`;
        aLink.href = convertCsvLinesToCsvData(results.csvLines);
        const event = new MouseEvent('click');
        aLink.dispatchEvent(event);
      },
    );
  },

  render() {
    app.BaseListView.prototype.render.apply(this, arguments);

    if (!this.collection.hasBeenFetched()) {
      this.listenToOnce(this.collection, 'reset', () => {
        if (app.sessionUser.isBite()) {
          this._renderSearchHint();
        }
      });
    }

    if (app.sessionUser.isBite()) {
      this._renderSearchHint();
    }

    if (app.sessionUser.isBiteOps()) {
      const $downloadButton = $(
        // prettier-ignore
        '<button type="button" class="btn btn-sm btn-primary download">' +
          'Download as CSV' +
        '</button>',
      );
      this.$('.card-header .right-button-container').prepend($downloadButton);
      $downloadButton.click(() => {
        this._exportOrgList();
      });
    }

    return this;
  },
});
